import Placeholder from "@tiptap/extension-placeholder";
import { Node as PMNode, Slice } from "@tiptap/pm/model";
import { Editor, EditorContent, EditorOptions, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { compact, noop } from "lodash";
import { MouseEvent, forwardRef, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import {
  canOpenInSidebar,
  getInternalPathname,
} from "@apps/main/routes-constants";
import { classNames, inputBorderClassName } from "@helpers/css";
import { parseStringToJSON, toWithBackground } from "@helpers/helpers";

import TipTapCustomImage from "./extensions/image";
import {
  WysiwygUploadVariable,
  asyncUploadImage,
} from "./extensions/image-helpers";
import { TextareaWysiwygExtensions } from "./textarea-wysiwyg-extensions";

const defaultFn = noop;

// A "header" line, e.g. the bold date headers the import dialog seeds. Used to decide where to
// keep a per-line placeholder visible (on the empty line directly under such a header).
const isBoldParagraph = (node: PMNode | null | undefined): boolean => {
  if (!node || node.type.name !== "paragraph" || !node.textContent)
    return false;
  let bold = false;
  node.descendants((child) => {
    if (child.isText && child.marks.some((m) => m.type.name === "bold")) {
      bold = true;
    }
  });
  return bold;
};

type TextareaWysiwygProps = {
  deps?: any[];
  value?: JSON | string | null;
  autofocus?: boolean;
  editable?: boolean;
  placeholder?: string;
  // When true, always show `placeholder` on an empty line directly below a bold "header" line
  // (e.g. the date headers the import dialog seeds), regardless of focus -- but not on other
  // empty lines (e.g. spacing lines). Default false keeps the standard "placeholder only when
  // the whole editor is empty" behavior for all other callers.
  showPlaceholderAfterBoldLine?: boolean;
  className?: string;
  fullWidth?: boolean;
  tiptapAiJwt?: string;
  overridePasteEvent?: boolean;
  imageUploadVariable?: WysiwygUploadVariable;
  onChangeValue?: (value: string, editor: Editor) => void;
  onFocus?: EditorOptions["onFocus"];
  onBlur?: EditorOptions["onBlur"];
};

const TextareaWysiwyg = forwardRef<Editor | null, TextareaWysiwygProps>(
  (
    {
      deps = [],
      value,
      overridePasteEvent,
      editable = false,
      autofocus = false,
      placeholder = "",
      showPlaceholderAfterBoldLine = false,
      className = "",
      fullWidth = false,
      imageUploadVariable,
      onChangeValue,
      onFocus = defaultFn,
      onBlur = defaultFn,
    },
    ref,
  ) => {
    const history = useHistory();
    const location = useLocation<TFLocationState>();
    const jsonValue = parseStringToJSON(value);

    const content = !value // if falsy, return null
      ? null
      : typeof value !== "string" // if not a string, then it's JSON so we return the json
        ? value
        : jsonValue // if value can be parsed to JSON then return the parsed JSON value
          ? jsonValue
          : value; // or we just return the string value.

    const editor = useEditor(
      {
        injectNonce: window.TF?.cspNonce,
        // Keep the Tiptap 2 default: re-render on every transaction.
        shouldRerenderOnTransaction: true,
        editable,
        autofocus,
        extensions: compact([
          StarterKit.configure({
            paragraph: false,
            codeBlock: false,
            bulletList: false,
            orderedList: false,
            listItem: false,
            // Tiptap 3 added these to StarterKit. The textarea extensions
            // add their own link and underline, and never had the others.
            link: false,
            underline: false,
            listKeymap: false,
            trailingNode: false,
          }),
          ...TextareaWysiwygExtensions,
          Placeholder.configure({
            // For the "after bold line" mode we decorate every empty node (showOnlyCurrent:false)
            // so the hint stays put regardless of focus, then the function below limits it to the
            // line right under a bold header. Otherwise keep the default (only the focused line).
            showOnlyCurrent: !showPlaceholderAfterBoldLine,
            placeholder: ({ editor, node, pos }) => {
              if (showPlaceholderAfterBoldLine) {
                if (node.type.name !== "paragraph") return "";
                // Also show it on a fully empty editor (e.g. no seeded date headers yet).
                if (editor.isEmpty) return placeholder;
                // Otherwise only the empty line right below a bold header (e.g. a date).
                const before = editor.state.doc.resolve(pos).nodeBefore;
                return isBoldParagraph(before) ? placeholder : "";
              }
              // Default: show the placeholder only when the whole editor is empty.
              return editor.isEmpty ? placeholder : "";
            },
          }),
          TipTapCustomImage(
            imageUploadVariable
              ? asyncUploadImage(imageUploadVariable)
              : undefined,
          ),
        ]),
        editorProps: {
          attributes: {
            class: editable
              ? classNames(
                  "prose max-w-full px-4 py-2 text-sm",
                  inputBorderClassName,
                  fullWidth && "max-w-none",
                  className,
                )
              : classNames(
                  "prose text-sm",
                  fullWidth && "max-w-none",
                  className,
                ),
          },
          transformPasted: overridePasteEvent ? () => Slice.empty : undefined,
        },
        content,
        onFocus,
        onBlur,
      },
      [...deps],
    );
    if (ref && typeof ref !== "function") {
      ref.current = editor;
    }

    useEffect(() => {
      const handleUpdateValue = ({ editor }: any) => {
        if (onChangeValue)
          onChangeValue(JSON.stringify(editor.getJSON()), editor);
      };
      editor?.on("update", handleUpdateValue);
      return function cleanup() {
        editor?.off("update", handleUpdateValue);
      };
    }, [editor, onChangeValue]);

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented) return;
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;

      event.preventDefault();
      const internalPathname = getInternalPathname(href);
      if (!internalPathname) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      history.push(
        canOpenInSidebar(href)
          ? toWithBackground({ pathname: internalPathname, location })
          : internalPathname,
      );
    };

    return (
      <>
        <EditorContent
          editor={editor}
          className="flex-1 min-w-0"
          onClick={handleClick}
        />
      </>
    );
  },
);

export default TextareaWysiwyg;
